import { addError, addLoading, getLoading, removeLoadingAndError } from '../core/redux/api/module';
import { hasTrippedBreaker, updateBreaker } from '../core/redux/api/circuitBreaker/module';
import { IAppState } from '../core/redux/IAppState';
import { ApiReducer, makeApiAction, makeApiReducer, RsaaTypes } from '../core/redux/api/reduxStandardApiAction';
import { apiGetTrainingDocuments } from '../core/redux/api/endpoints';
import { ACTION_TYPE } from '../core/redux/actionType';
import { sortTrainingDocuments, TrainingDocument } from './trainingDocument';
import { assoc } from '../language/module';


const KEY_ACTION = ACTION_TYPE.UPLOAD_TRAINING_DOC_REQUEST;
const ACTIONS    = [
  ACTION_TYPE.UPLOAD_TRAINING_DOC_REQUEST,
  ACTION_TYPE.UPLOAD_TRAINING_DOC_RESPONSE,
  ACTION_TYPE.UPLOAD_TRAINING_DOC_ERROR,
];


/**
 * Creates an action to dispatch that uploads a new training document. The API authorizes writes by the caller's role,
 * sent as a query parameter. The body is multipart form data, so the headers deliberately omit content-type — the
 * browser sets it with the multipart boundary.
 * @param file the .docx to upload
 * @param title display title for the document list
 * @param sortOrder optional position in the document list (unset sorts last)
 */
export const uploadTrainingDocumentAction = (file: File, title: string, sortOrder?: number) => makeApiAction(
  {
    method:   'POST',
    endpoint: (state: IAppState) => `${apiGetTrainingDocuments}?role=${encodeURIComponent(state.userState?.role ?? '')}`,
    headers:  (state: IAppState) => ({
      'Authorization': `Bearer ${state.auth.bearerToken}`,
    }),
    body:     (state: IAppState) => {
      const formData = new FormData();
      formData.append('File', file);
      formData.append('Title', title);
      if (sortOrder != null) {
        formData.append('SortOrder', String(sortOrder));
      }
      formData.append('UploadedBy', state.userState.userName);
      return formData;
    },
    types:    ACTIONS as RsaaTypes,
    bailout:  state => !!getLoading(state, KEY_ACTION, KEY_ACTION) || hasTrippedBreaker(state, KEY_ACTION),
  },
);


const reducer: ApiReducer<TrainingDocument> = (state, action, error, isLoading, payload) => {
  if (isLoading) {
    return addLoading(state, KEY_ACTION, KEY_ACTION);
  }

  const withBreaker = updateBreaker(state, KEY_ACTION, !!error);

  if (error) {
    return addError(withBreaker, KEY_ACTION, KEY_ACTION, error);
  }

  const noLoadingOrError = removeLoadingAndError(withBreaker, KEY_ACTION, KEY_ACTION);

  return assoc(
    'trainingDocuments',
    sortTrainingDocuments([ ...(noLoadingOrError.trainingDocuments ?? []), payload! ]),
    noLoadingOrError,
  );
};


export const uploadTrainingDocumentReducer = makeApiReducer<TrainingDocument>(ACTIONS, KEY_ACTION, reducer);
